/**
 * What a row in the outline is trying to say.
 *
 * The rail had badges already, and the complaint about them was specific: a
 * clause somebody had annotated looked the same as one nobody had opened. Both
 * rows were grey text on paper, and the `●` that separated them was drawn in
 * `--color-muted` at 10px — the quietest ink in the interface, spent on the one
 * fact a rep is scanning 235 rows to find.
 *
 * Two rules do the work now.
 *
 * The six badges are the mockup's six, in the mockup's order, so the legend at
 * the foot of the rail reads left to right in the same sequence the rows do.
 *
 * And the palette splits on provenance rather than on subject: the three things
 * a *person* did — a note, an open question, a claim — are ink, and the three
 * things the *workbench* found — a defective reference, an Award comparison, a
 * silence — keep the semantic palette. So interaction is the darkest thing in a
 * grey rail, which is the whole point, and every badge still pairs its glyph
 * with its colour, so none of it rests on hue alone.
 */

import type { TreeNode } from '../lib/api.js';

export interface BadgeKind {
  key: 'broken' | 'protect' | 'silent' | 'annotations' | 'questions' | 'claims';
  glyph: string;
  /** The word in the legend, as the mockup writes it. */
  name: string;
  colour: string;
  /** The sentence the info button reveals. */
  meaning: string;
}

/** The legend at the foot of the rail, and the order badges appear in a row. */
export const BADGE_LEGEND: readonly BadgeKind[] = [
  {
    key: 'broken',
    glyph: '△',
    name: 'broken',
    colour: 'var(--color-defect)',
    meaning: 'A cross-reference in this clause points somewhere wrong.',
  },
  {
    key: 'protect',
    glyph: '◇',
    name: 'beats award',
    colour: 'var(--color-win)',
    meaning: 'This clause is better than the legal floor — protect it.',
  },
  {
    key: 'silent',
    glyph: '∅',
    name: 'silent',
    colour: 'var(--color-silent)',
    meaning: 'No equivalent provision exists anywhere.',
  },
  {
    key: 'annotations',
    glyph: '●',
    name: 'notes',
    colour: 'var(--color-ink)',
    meaning: 'Notes and highlights somebody has left on these words.',
  },
  {
    key: 'questions',
    glyph: '?',
    name: 'open',
    colour: 'var(--color-ink)',
    meaning: 'Unanswered questions logged against this clause.',
  },
  {
    key: 'claims',
    glyph: '□',
    name: 'claims',
    colour: 'var(--color-ink)',
    meaning: 'Claims anchored here.',
  },
];

export interface RailBadge {
  key: BadgeKind['key'];
  /** Glyph, plus the count where a count is the finding. */
  text: string;
  colour: string;
  /** Read out by a screen reader, and shown on hover. */
  label: string;
}

const plural = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;

/** The badges this row carries, in legend order. */
export function badgesFor(node: TreeNode): RailBadge[] {
  const b = node.badges;
  const out: RailBadge[] = [];

  for (const kind of BADGE_LEGEND) {
    switch (kind.key) {
      case 'broken':
        if (b.brokenRef > 0) {
          out.push({
            key: kind.key,
            text: kind.glyph,
            colour: kind.colour,
            label: plural(b.brokenRef, 'defective reference', 'defective references'),
          });
        }
        break;
      case 'protect':
        if (b.protect) {
          out.push({
            key: kind.key,
            text: kind.glyph,
            colour: kind.colour,
            label: 'Above the Award floor',
          });
        }
        break;
      case 'silent':
        if (b.silent) {
          out.push({
            key: kind.key,
            text: kind.glyph,
            colour: kind.colour,
            label: 'No equivalent anywhere',
          });
        }
        break;
      case 'annotations':
        if (b.annotations > 0) {
          out.push({
            key: kind.key,
            text: `${kind.glyph}${b.annotations}`,
            colour: kind.colour,
            label: plural(b.annotations, 'mark on this clause', 'marks on this clause'),
          });
        }
        break;
      case 'questions':
        if (b.questions > 0) {
          out.push({
            key: kind.key,
            text: `${kind.glyph}${b.questions}`,
            colour: kind.colour,
            label: plural(b.questions, 'open question', 'open questions'),
          });
        }
        break;
      case 'claims':
        if (b.claims > 0) {
          out.push({
            key: kind.key,
            text: `${kind.glyph}${b.claims}`,
            colour: kind.colour,
            label: plural(b.claims, 'claim', 'claims'),
          });
        }
        break;
    }
  }

  return out;
}

/**
 * Has anyone actually worked on this clause?
 *
 * Narrower than `carriesSomething` on purpose, and the difference is the point
 * of the whole change. A clause can carry a defective reference and an Award
 * comparison without a single person having looked at it — those are findings
 * the ingest produced. A note, a question or a claim means somebody has been
 * here, and that is what a rep opening the rail wants to see from across the
 * row rather than by reading a 10px glyph at the end of it.
 */
export function isWorked(node: TreeNode): boolean {
  const b = node.badges;
  return b.annotations > 0 || b.questions > 0 || b.claims > 0;
}

/**
 * Does this clause carry anything worth a place in a shortened outline?
 *
 * The full outline is 235 rows, which is honest but is a table of contents —
 * it tells you the agreement's shape and nothing about where the work is. The
 * attention view answers a different question: which clauses has somebody
 * already found something in. A defective reference, a published mark, an
 * open question, a claim, a protect flag, a silence.
 */
export function carriesSomething(node: TreeNode): boolean {
  const b = node.badges;
  return isWorked(node) || b.brokenRef > 0 || b.protect || b.silent;
}
